import React from "react";

import { nextApiPath } from "@/api/nextApiPaths";
import { getAbsoluteSiteUrl, getCanonicalPath, getCanonicalSiteUrl, getSiteBaseUrl } from "@/utils/siteUrl";
import Head from "next/head";
import { useRouter } from "next/router";

interface SEOHeadProps {
  title?: string;
  description?: string;
  ogImage?: string;
  noindex?: boolean;
  keywords?: string;
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  structuredData?: object | object[]; // JSON-LD structured data
}

/**
 * SEO Head component with canonical URL support
 *
 * Follows modern SEO best practices including:
 * - Canonical URLs to prevent duplicate content
 * - Open Graph metadata for social sharing
 * - Twitter Cards for enhanced Twitter previews
 * - Structured data support
 * - Proper meta tags for search engines
 *
 * @example
 * ```tsx
 * <SEOHead
 *   title="Sales Pipeline"
 *   description="Manage your sales pipeline"
 *   keywords="sales, pipeline, CRM"
 * />
 * ```
 */
export const SEOHead: React.FC<SEOHeadProps> = ({
  title,
  description,
  ogImage,
  noindex = false,
  keywords,
  author,
  publishedTime,
  modifiedTime,
  structuredData
}) => {
  const router = useRouter();
  const baseUrl = getSiteBaseUrl();
  const isKorean = router.locale === "ko";
  const brandName = isKorean ? "워크사인" : "Worksine";

  // Remove query parameters and hash for canonical URL
  const canonicalPath = getCanonicalPath(router.asPath);
  const canonicalUrl = getCanonicalSiteUrl(canonicalPath);

  // Default OG image with proper dimensions
  const defaultOgImage = `${baseUrl}${nextApiPath("/og")}`;
  const ogImageUrl = ogImage ? getAbsoluteSiteUrl(ogImage) : defaultOgImage;

  // Full title with brand
  const fullTitle = title ? `${title} | ${brandName}` : brandName;

  // Organization structured data (always included)
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: brandName,
    url: baseUrl,
    logo: `${baseUrl}/Sellpath-logo.png`,
    description: isKorean ? "AI 기반 영업 관리 플랫폼" : "AI-powered sales management platform",
    sameAs: [
      // Add your social media URLs here
    ]
  };

  // Combine organization schema with custom structured data
  const allStructuredData = structuredData
    ? Array.isArray(structuredData)
      ? [organizationSchema, ...structuredData]
      : [organizationSchema, structuredData]
    : [organizationSchema];

  return (
    <Head>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      {keywords && <meta name="keywords" content={keywords} />}
      {(author || brandName) && <meta name="author" content={author || brandName} />}

      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />

      {/* Robots */}
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonicalUrl} />
      {title && <meta property="og:title" content={fullTitle} />}
      {description && <meta property="og:description" content={description} />}
      <meta property="og:image" content={ogImageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={brandName} />
      <meta property="og:locale" content={router.locale === "ko" ? "ko_KR" : "en_US"} />
      {publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      {title && <meta name="twitter:title" content={fullTitle} />}
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={ogImageUrl} />

      {/* Additional SEO tags */}
      <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=5" />
      <meta httpEquiv="x-ua-compatible" content="ie=edge" />

      {/* Structured Data (JSON-LD) */}
      {allStructuredData.map((schema, index) => (
        <script
          key={`structured-data-${index}`}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />
      ))}
    </Head>
  );
};

export default SEOHead;
